import { fetchShortList, fetchUploaderList } from 'api'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useImmer } from 'use-immer'
import {
  CustomInfiniteScrollCmsGridWrapper,
  EmptyTextWrapper,
  Header,
  IconBack,
  OverlayTitle,
  StyledBase64Img,
  TotalShortObserverArea,
  TotalShorts,
  Uploader,
  UploaderImage,
  UploaderMeta,
  UploaderMetaName,
  UploaderMetaValue,
  UploaderWrapper,
  Wrapper,
} from './Styled'
import VideoBoxVertical from 'component/VideoBoxVertical'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useParamsOfPage } from 'hooks/useNavigateTo'
import { useAppNavigate } from 'app-layered-layout/useAppNavigate'
import { catchPromiseCancel } from 'utils/catchPromiseCancel'
import themeAssetShortBackgroundUploaderBackgroundPng from '@theme/short-background/uploader-background.png'
import EmptyResult from 'component/EmptyResult'

const createInitialUploaderData = () => ({
  cover_path: '',
  created_at: '',
  description: '',
  fans_count: 0,
  favorite_count: 0,
  icon_path: '',
  id: '0',
  like_count: 0,
  name: '',
  status: 0,
  updated_at: '',
  video_num: 0,
})

const createInitialUploaderShorts = (hasMore = false) => ({
  nextPage: 1,
  isLoading: hasMore,
  hasMore,
  list: [] as any[],
  page_result: { current: null as any, pageSize: null as any, total: null as any },
})

const ViewUploader = () => {
  const { uploaderId } = useParamsOfPage('uploader')
  const navigate = useAppNavigate()
  const [uploaderAvailable, setUploaderAvailable] = useState<boolean | null>(null)

  const { ref, containerRef, ratio } = useIntersectionObserver<HTMLDivElement, HTMLDivElement>({
    threshold: Array.from({ length: 19 }).map((_, index) => (index + 1) / 19),
    defaultVisible: true,
  })

  const expandRange = (current: number, [rangeMin, rangeMax]: [number, number], [toMin, toMax]: [number, number]) => {
    const expanded = ((current - rangeMin) / (rangeMax - rangeMin)) * (toMax - toMin) + toMin
    const capped = Math.min(toMax, Math.max(toMin, expanded))
    return capped
  }

  const mappedRatio = expandRange(ratio, [1, 0.95], [0, 1])

  const [uploaderData, updateUploaderData] = useImmer(createInitialUploaderData())

  const [uploaderShorts, updateUploaderShorts] = useImmer(createInitialUploaderShorts())
  const fetching = useRef(false)
  const shortsControllerRef = useRef<AbortController | null>(null)

  // get uploader data at page load
  useEffect(() => {
    const controller = new AbortController()
    shortsControllerRef.current?.abort()
    fetching.current = false
    setUploaderAvailable(null)
    updateUploaderData(createInitialUploaderData())
    updateUploaderShorts(createInitialUploaderShorts())

    const data = {
      id: uploaderId,
      signal: controller.signal,
    }
    fetchUploaderList(data)
      .then((response: any) => {
        const uploader = Array.isArray(response?.data) ? response.data[0] : null
        if (uploader) {
          updateUploaderData((draft) => {
            Object.assign(draft, uploader)
          })
          setUploaderAvailable(true)
          updateUploaderShorts(createInitialUploaderShorts(true))
        } else {
          updateUploaderData(createInitialUploaderData())
          updateUploaderShorts(createInitialUploaderShorts())
          setUploaderAvailable(false)
        }
      })
      .catch((error: any) => {
        catchPromiseCancel(error)
        if (!controller.signal.aborted) {
          updateUploaderData(createInitialUploaderData())
          updateUploaderShorts(createInitialUploaderShorts())
          setUploaderAvailable(false)
        }
      })

    return () => {
      controller.abort()
      shortsControllerRef.current?.abort()
    }
  }, [updateUploaderData, updateUploaderShorts, uploaderId])

  const style = useMemo(
    () => ({
      display: 'flex',
      gap: 'var(--cms-grid-row-gap, 0.093rem) var(--cms-grid-column-gap, 0.093rem)',
      flexWrap: 'wrap' as const,
    }),
    []
  )

  const handleFetchVideoList = () => {
    if (uploaderAvailable !== true) return

    if (!uploaderShorts.hasMore) return

    if (fetching.current) return
    fetching.current = true
    const controller = new AbortController()
    shortsControllerRef.current = controller

    const data = {
      current: uploaderShorts.nextPage,
      pageSize: 30,
      // WARN: force cast string to number because js do not support int64.
      uploader_id: uploaderId as unknown as number,
      signal: controller.signal,
    }

    fetchShortList(data)
      .then((response: any) => {
        const list = Array.isArray(response?.data) ? response.data : []
        const pageResult = response?.page_result

        updateUploaderShorts((draft) => {
          draft.list = draft?.list?.concat(list)
          if (pageResult) {
            draft.hasMore = pageResult.total > pageResult.current * pageResult.pageSize
            draft.nextPage = pageResult.current + 1
            draft.page_result = pageResult
          } else {
            draft.hasMore = false
          }

          draft.isLoading = false
        })
      })
      .catch((error: any) => {
        catchPromiseCancel(error)
        if (!controller.signal.aborted) {
          updateUploaderShorts((draft) => {
            draft.hasMore = false
            draft.isLoading = false
          })
        }
      })
      .finally(() => {
        if (shortsControllerRef.current === controller) {
          shortsControllerRef.current = null
        }
        if (!controller.signal.aborted) {
          fetching.current = false
        }
      })
  }

  const shortInfo = useMemo(() => {
    return {
      list: uploaderShorts.list,
    }
  }, [uploaderShorts.list])

  return (
    <>
      <Wrapper ref={containerRef}>
        <Header>
          <UploaderWrapper>
            <Uploader>{uploaderAvailable ? uploaderData.name : '--'}</Uploader>
            <UploaderMeta>
              <UploaderMetaValue>{uploaderAvailable ? uploaderData.like_count : '--'}</UploaderMetaValue>
              <UploaderMetaName>获赞</UploaderMetaName>
              <UploaderMetaValue>{uploaderAvailable ? uploaderData.favorite_count : '--'}</UploaderMetaValue>
              <UploaderMetaName>被收藏</UploaderMetaName>
            </UploaderMeta>
          </UploaderWrapper>
          <UploaderImage>
            <StyledBase64Img
              noLazy
              src={uploaderData.icon_path}
              imageStyle={{
                objectPosition: 'top',
              }}
            ></StyledBase64Img>
          </UploaderImage>
          <StyledBase64Img
            src={uploaderData.cover_path}
            placeholder={themeAssetShortBackgroundUploaderBackgroundPng}
            aspectRatio={null}
          ></StyledBase64Img>
        </Header>
        <TotalShorts>
          她的作品 {uploaderAvailable === true && uploaderShorts.page_result.total}
          <TotalShortObserverArea ref={ref} />
        </TotalShorts>
        {uploaderAvailable === true &&
          (uploaderShorts.list.length > 0 || uploaderShorts.isLoading || uploaderShorts.hasMore) && (
            <CustomInfiniteScrollCmsGridWrapper
              style={style}
              pageStart={0}
              loadMore={handleFetchVideoList}
              hasMore={uploaderShorts?.hasMore}
              // loader={<div className="loader" key={0}>Loading ...</div>}
              useWindow={false}
            >
              {uploaderShorts?.list?.map((item, i) => {
                return (
                  <VideoBoxVertical key={item.id ?? i} noTitle columns={3} videoInfo={item} shortInfo={shortInfo} />
                )
              })}
            </CustomInfiniteScrollCmsGridWrapper>
          )}
        {uploaderAvailable !== null && uploaderShorts.list.length === 0 && !uploaderShorts.isLoading && (
          <EmptyTextWrapper>
            <EmptyResult text={uploaderAvailable ? '暂无内容' : '无此UP主资料'} />
          </EmptyTextWrapper>
        )}
      </Wrapper>
      <OverlayTitle style={{ opacity: mappedRatio }}>{uploaderAvailable ? uploaderData.name : ''}</OverlayTitle>
      <IconBack aria-label="返回" onClick={() => navigate(-1)}></IconBack>
    </>
  )
}

export default ViewUploader
